import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Plus, Trash2, Save } from "lucide-react";
import FortuneWheelDisk from "@/components/FortuneWheelDisk";

const EMPTY_SLICE = { label: "5% OFF", type: "percent", value: 5, weight: 2, color: "#C9A07A" };

function formatWhen(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function AdminFortuneWheel() {
  const [active, setActive] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("wheel");
  const [customers, setCustomers] = useState([]);
  const [customersLoaded, setCustomersLoaded] = useState(false);

  useEffect(() => {
    api.get("/merchant/config/fortune-wheel").then(({ data }) => {
      setActive(Boolean(data.active));
      setCfg(data.fortune_wheel);
    }).catch((e) => toast.error(extractErr(e)));
  }, []);

  useEffect(() => {
    if (!active || tab !== "customers" || customersLoaded) return;
    api.get("/merchant/config/fortune-wheel/customers").then(({ data }) => {
      setCustomers(data.items || []);
      setCustomersLoaded(true);
    }).catch((e) => toast.error(extractErr(e)));
  }, [active, tab, customersLoaded]);

  if (!cfg) return <div className="text-slate-500">Loading…</div>;
  if (!active) {
    return (
      <div className="max-w-2xl space-y-4">
        <Link to="/admin/addons" className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> App Store</Link>
        <Card className="p-8 text-center text-slate-600">Unlock <strong>Fortune of Wheel</strong> from the App Store to configure prizes.</Card>
      </div>
    );
  }

  const setSlice = (i, patch) => {
    const slices = [...(cfg.slices || [])];
    slices[i] = { ...slices[i], ...patch };
    setCfg({ ...cfg, slices });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/merchant/config/fortune-wheel", cfg);
      setCfg(data.fortune_wheel);
      toast.success("Wheel saved");
    } catch (e) { toast.error(extractErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl space-y-5">
      <Link to="/admin/addons" className="text-sm text-slate-500 inline-flex items-center gap-1" data-testid="fw-back"><ArrowLeft className="w-4 h-4" /> App Store</Link>
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Fortune of Wheel</h1>
        <p className="text-sm text-slate-500 mt-1">Shoppers enter their mobile number, then spin for a one-time coupon. Weights control odds. Use type “none” for try-again slices.</p>
      </div>
      <div className="flex items-center gap-2 border-b border-slate-200">
        <button type="button" data-testid="fw-tab-wheel" onClick={() => setTab("wheel")} className={`px-4 py-2.5 text-sm font-semibold -mb-px border-b-2 ${tab === "wheel" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500"}`}>Wheel</button>
        <button type="button" data-testid="fw-tab-customers" onClick={() => setTab("customers")} className={`px-4 py-2.5 text-sm font-semibold -mb-px border-b-2 ${tab === "customers" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500"}`}>Customer Data</button>
      </div>
      {tab === "customers" ? (
        <Card className="p-0 overflow-hidden border-slate-200/70" data-testid="fw-customers">
          {customers.length === 0 ? (
            <div className="p-8 text-sm text-slate-500 text-center">{customersLoaded ? "No spins yet. Numbers appear here after a shopper spins." : "Loading…"}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">Mobile</th>
                    <th className="px-4 py-3 font-medium">Prize</th>
                    <th className="px-4 py-3 font-medium">Coupon</th>
                    <th className="px-4 py-3 font-medium">When</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {customers.map((row, i) => (
                    <tr key={`${row.phone}-${row.created_at}-${i}`} data-testid={`fw-customer-${i}`}>
                      <td className="px-4 py-3 font-mono-nums">{row.phone ? `+91 ${row.phone}` : "—"}</td>
                      <td className="px-4 py-3">{row.label || "—"}</td>
                      <td className="px-4 py-3 font-mono-nums">{row.coupon_code || "—"}</td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatWhen(row.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ) : null}
      {tab === "wheel" ? (<>
      <div className="rounded-[28px] overflow-hidden border border-stone-200 grid md:grid-cols-2 bg-white shadow-sm" data-testid="fw-preview">
        <div className="flex items-center justify-center py-8" style={{ background: "radial-gradient(circle at 50% 40%, #fff7f2 0%, #f8d7dc 55%, #f3c4c8 100%)" }}>
          <FortuneWheelDisk slices={cfg.slices || []} size={220} hubLabel="SPIN" testId="fw-preview-disk" />
        </div>
        <div className="p-8 text-center flex flex-col justify-center">
          <div className="font-display text-2xl font-bold text-stone-900">{cfg.headline || "Register To Win A Reward"}</div>
          <p className="text-sm text-stone-500 mt-2">{cfg.subhead}</p>
          <div className="mt-6 h-11 rounded-lg bg-[#3D2B1F] text-white text-sm font-semibold tracking-wide uppercase flex items-center justify-center">{cfg.cta || "SPIN TO WIN"}</div>
        </div>
      </div>
      <Card className="p-6 space-y-4 border-slate-200/70">
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Headline</Label><Input className="mt-1" data-testid="fw-headline" value={cfg.headline || ""} onChange={(e) => setCfg({ ...cfg, headline: e.target.value })} /></div>
          <div><Label>Button text</Label><Input className="mt-1" data-testid="fw-cta" value={cfg.cta || ""} onChange={(e) => setCfg({ ...cfg, cta: e.target.value })} /></div>
        </div>
        <div><Label>Subhead</Label><Input className="mt-1" data-testid="fw-subhead" value={cfg.subhead || ""} onChange={(e) => setCfg({ ...cfg, subhead: e.target.value })} /></div>
        <div>
          <Label>How often can someone spin?</Label>
          <select className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="fw-frequency" value={cfg.frequency || "once"} onChange={(e) => setCfg({ ...cfg, frequency: e.target.value })}>
            <option value="once">Once ever</option>
            <option value="daily">Once per day</option>
            <option value="always">Every visit</option>
          </select>
        </div>
      </Card>
      <Card className="p-6 space-y-4 border-slate-200/70">
        <div className="flex items-center justify-between">
          <Label>Slices (3–8)</Label>
          {(cfg.slices || []).length < 8 && (
            <Button type="button" size="sm" variant="outline" data-testid="fw-slice-add" onClick={() => setCfg({ ...cfg, slices: [...(cfg.slices || []), { ...EMPTY_SLICE }] })}>
              <Plus className="w-4 h-4 mr-1" /> Add slice
            </Button>
          )}
        </div>
        {(cfg.slices || []).map((s, i) => (
          <div key={i} className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end p-3 rounded-xl border border-slate-200 bg-slate-50/70" data-testid={`fw-slice-${i}`}>
            <div className="sm:col-span-2"><Label className="text-xs">Label</Label><Input value={s.label} onChange={(e) => setSlice(i, { label: e.target.value })} /></div>
            <div>
              <Label className="text-xs">Type</Label>
              <select className="mt-1 w-full h-10 rounded-md border border-slate-200 px-2 text-sm" value={s.type} onChange={(e) => setSlice(i, { type: e.target.value })}>
                <option value="percent">% off</option>
                <option value="flat">₹ off</option>
                <option value="none">Try again</option>
              </select>
            </div>
            <div><Label className="text-xs">Value</Label><Input type="number" value={s.value} onChange={(e) => setSlice(i, { value: e.target.value })} /></div>
            <div><Label className="text-xs">Weight</Label><Input type="number" min={1} value={s.weight} onChange={(e) => setSlice(i, { weight: e.target.value })} /></div>
            <div className="flex items-center gap-2">
              <Input type="color" className="h-10 w-12 p-1" value={s.color || "#7c3aed"} onChange={(e) => setSlice(i, { color: e.target.value })} />
              {(cfg.slices || []).length > 3 && (
                <button type="button" className="text-red-600" onClick={() => setCfg({ ...cfg, slices: cfg.slices.filter((_, j) => j !== i) })}><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          </div>
        ))}
      </Card>
      <Button onClick={save} disabled={saving} data-testid="fw-save" className="bg-violet-600 hover:bg-violet-700"><Save className="w-4 h-4 mr-1" /> Save wheel</Button>
      </>) : null}
    </div>
  );
}
